/**
 * Plan Schema — single source of truth for all plan-controllable fields.
 *
 * Categories:
 *   quota    – numeric caps (0 = unlimited)
 *   feature  – boolean on/off toggles
 *   operational – behavioral limits
 *
 * Each entry contains:
 *   key          – field name used in API and PlanLimitsManager
 *   label        – human-readable label for UI
 *   category     – quota | feature | operational
 *   type         – "number" | "boolean" | "string"
 *   defaultValue – fallback when plan doesn't specify this field
 *   description  – tooltip / explanation
 */

// ---------------------------------------------------------------------------
// Resource Quotas
// ---------------------------------------------------------------------------
export const QUOTA_FIELDS = [
    {
        key: "max_workspaces",
        label: "Max Workspaces",
        category: "quota",
        type: "number",
        defaultValue: 0,          // 0 = unlimited
        description: "Maximum number of workspaces (brands) the account can own.",
    },
    {
        key: "max_locations",
        label: "Max Locations",
        category: "quota",
        type: "number",
        defaultValue: 0,
        description: "Maximum number of locations (branches) per workspace.",
    },
    {
        key: "max_screens",
        label: "Max Screens",
        category: "quota",
        type: "number",
        defaultValue: 0,
        description: "Maximum total screens across all workspaces.",
        apiAlias: "screens_limit",   // maps to existing API field
    },
    {
        key: "max_screens_per_location",
        label: "Max Screens per Location",
        category: "quota",
        type: "number",
        defaultValue: 0,
        description: "Maximum screens within a single location.",
    },
    {
        key: "max_storage_mb",
        label: "Max Storage (MB)",
        category: "quota",
        type: "number",
        defaultValue: 0,
        description: "Total media storage in megabytes.",
        apiAlias: "storage_limit",   // maps to existing API field
    },
    {
        key: "max_file_size_mb",
        label: "Max File Size (MB)",
        category: "quota",
        type: "number",
        defaultValue: 1024,          // 1 GB default
        description: "Maximum single-file upload size in megabytes.",
    },
    {
        key: "max_users",
        label: "Max Team Members",
        category: "quota",
        type: "number",
        defaultValue: 0,
        description: "Maximum sub-users / team members under this account.",
    },
    {
        key: "max_roles",
        label: "Max Custom Roles",
        category: "quota",
        type: "number",
        defaultValue: 0,
        description: "Maximum custom roles that can be created.",
    },
    {
        key: "max_playlists",
        label: "Max Playlists",
        category: "quota",
        type: "number",
        defaultValue: 0,
        description: "Maximum playlists per workspace.",
    },
    {
        key: "max_campaigns",
        label: "Max Active Campaigns",
        category: "quota",
        type: "number",
        defaultValue: 0,
        description: "Maximum active campaigns.",
    },
];

// ---------------------------------------------------------------------------
// Feature Access Toggles
// ---------------------------------------------------------------------------
export const FEATURE_FIELDS = [
    {
        key: "feature_analytics",
        label: "Analytics",
        category: "feature",
        type: "boolean",
        defaultValue: true,
        description: "Access to the analytics page.",
    },
    {
        key: "feature_advanced_analytics",
        label: "Advanced Analytics",
        category: "feature",
        type: "boolean",
        defaultValue: false,
        description: "Deep analytics (online status, heatmaps, etc.).",
    },
    {
        key: "feature_campaigns",
        label: "Campaigns",
        category: "feature",
        type: "boolean",
        defaultValue: true,
        description: "Access to campaign management.",
    },
    {
        key: "feature_events",
        label: "Events",
        category: "feature",
        type: "boolean",
        defaultValue: true,
        description: "Access to event scheduling.",
    },
    {
        key: "feature_widgets",
        label: "Widgets",
        category: "feature",
        type: "boolean",
        defaultValue: true,
        description: "Access to custom widgets.",
    },
    {
        key: "feature_timelines",
        label: "Timelines",
        category: "feature",
        type: "boolean",
        defaultValue: true,
        description: "Access to timeline scheduling.",
    },
    {
        key: "feature_activity_log",
        label: "Activity Log",
        category: "feature",
        type: "boolean",
        defaultValue: true,
        description: "Access to activity log.",
    },
    {
        key: "feature_media_log",
        label: "Media Log",
        category: "feature",
        type: "boolean",
        defaultValue: true,
        description: "Access to media log.",
    },
    {
        key: "feature_license_management",
        label: "License Management",
        category: "feature",
        type: "boolean",
        defaultValue: true,
        description: "Access to license management page.",
    },
    {
        key: "feature_custom_roles",
        label: "Custom Roles",
        category: "feature",
        type: "boolean",
        defaultValue: false,
        description: "Ability to create/edit custom roles (vs. predefined only).",
    },
    {
        key: "feature_api_access",
        label: "API Access",
        category: "feature",
        type: "boolean",
        defaultValue: false,
        description: "Access to external API / integrations.",
    },
    {
        key: "feature_white_label",
        label: "White Label",
        category: "feature",
        type: "boolean",
        defaultValue: false,
        description: "Custom branding / white-label capability.",
    },
    {
        key: "feature_brand_content_sharing",
        label: "Content Sharing",
        category: "feature",
        type: "boolean",
        defaultValue: false,
        description: "Cross-workspace content sharing mode.",
    },
];

// ---------------------------------------------------------------------------
// Operational Limits
// ---------------------------------------------------------------------------
export const OPERATIONAL_FIELDS = [
    {
        key: "retention_days",
        label: "Log Retention (days)",
        category: "operational",
        type: "number",
        defaultValue: 30,
        description: "Days of activity/media log retention.",
    },
    {
        key: "support_level",
        label: "Support Level",
        category: "operational",
        type: "string",
        defaultValue: "basic",
        description: "Support tier: basic | priority | dedicated.",
    },
];

// ---------------------------------------------------------------------------
// Mapping: feature key → Constants page ID (for side menu gating)
// ---------------------------------------------------------------------------
export const FEATURE_TO_PAGE_MAP = {
    feature_analytics: "analytics",
    feature_campaigns: "campaign",
    feature_events: "event",
    feature_widgets: "widget",
    feature_timelines: "timeline",
    feature_activity_log: "activitylog",
    feature_media_log: "medialog",
    feature_license_management: "license",
};

// Reverse map: page ID → feature key
export const PAGE_TO_FEATURE_MAP = Object.fromEntries(
    Object.entries(FEATURE_TO_PAGE_MAP).map(([feat, page]) => [page, feat])
);

// ---------------------------------------------------------------------------
// All fields combined (handy for iteration)
// ---------------------------------------------------------------------------
export const ALL_PLAN_FIELDS = [
    ...QUOTA_FIELDS,
    ...FEATURE_FIELDS,
    ...OPERATIONAL_FIELDS,
];

// Quick lookup by key
export const PLAN_FIELD_MAP = Object.fromEntries(
    ALL_PLAN_FIELDS.map((f) => [f.key, f])
);
